import React from 'react'
import PropTypes from 'prop-types'
import { Table } from '@lmc/ui-kit'
import dayjs from 'dayjs'

import {
  filterMedications,
  getMARChartTableData,
  generateTableColumns,
  getTableStartDate,
} from './utils'
import { EMARLegend } from '../components'
import SingleCheckboxField from '../../../../../components/fields/types/SingleCheckboxField'
import { RangePicker } from '@lmc/ui-kit'
import MedicationListItem from './MedicationListItem'
import styled from 'styled-components'

const TableStyled = styled(Table)`
  th {
    // there is a bug in antd that doesn't allow to override the font-family
    font-family: ${(props) => props.theme.font.family.sansSerif} !important;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 400 !important;
    background: ${(props) => props.theme.palette.white} !important;
    border-right: 1px solid ${(props) => props.theme.palette.grey} !important;
    min-width: 33px !important;
    padding: 12px 0px !important;
    text-align: center !important;
    color: ${(props) => props.theme.palette.silver} !important;
  }
`

/**
@description Renders the legend of reason codes.

@returns {function} renderLegend
*/
const renderLegend = (props) => {
  const { displayLegend = true } = props

  return (
    <div style={styles.legendContainer}>
      <EMARLegend displayLegend={displayLegend} />
    </div>
  )
}

// Render Table Header Functions

/**
@description Renders the table date picker.

@param {array} dates. Array containing the start and
end date. [startDate, endDate]. Both dates are dayjs
objects.

Dates can be null if the dates have been cleared, and in
this case the startDate is set to 14 days in the past from
todayDate and the endDate is set to todayDate.

*/

const handleDateChange = (props) => {
  const { dates, setEndDate, setStartDate, todayDate } = props

  if (!dates) {
    setStartDate(dayjs(todayDate).subtract(14, 'days'))
    setEndDate(dayjs(todayDate))
  } else {
    setStartDate(dates[0])
    setEndDate(dates[1])
  }
}

/**
@description Renders the table date picker
*/
const renderDatePicker = (props) => {
  const { endDate, startDate } = props

  return (
    <div style={styles.datePickerContainer}>
      <RangePicker
        defaultValue={[startDate, endDate]}
        onChange={(dates) => {
          handleDateChange({ ...props, dates })
        }}
      />
    </div>
  )
}

/**
@description Renders the table medication list toggle

@param {bool} viewMedList. Stores whether or not to display
medication list only.

@param {function} setViewMedList. Sets viewMedList.

@returns {function} renderMedicationListToggle
*/
const renderMedicationListToggle = (props) => {
  const { viewMedList, setViewMedList } = props

  return (
    <div style={styles.medListToggleContainer}>
      <SingleCheckboxField
        compact
        label="View Medication List Only"
        formKey="viewMedList"
        values={{ viewMedList }}
        onChange={(output) => {
          setViewMedList(output.value)
        }}
      />
    </div>
  )
}

/**
@description Renders the table header

@param {bool} displayDatePicker. Toggles whether to display the date picker.

@param {bool} displayMedListToggle. Toggles whether to display the med list toggle.

@param {bool} displayTableHeader. Toggles whether to display the entire table header.

@returns {function} renderTableHeader
*/
const renderTableHeader = (props) => {
  const {
    displayDatePicker = true,
    displayMedListToggle = true,
    displayTableHeader = true,
    isPDF,
  } = props

  if (!displayTableHeader) return null

  return (
    <div style={styles.tableHeaderContainer}>
      {displayDatePicker && renderDatePicker(props)}
      {!isPDF && displayMedListToggle && renderMedicationListToggle(props)}
    </div>
  )
}

/**
@description Renders the medication list.

@param {array} medications. The medications to be rendered.

@returns {function} renderMedList
*/
const renderMedList = (props) => {
  const { medications, startDate, endDate } = props

  const filteredMedications = filterMedications(medications, startDate, endDate)

  return (
    <div>
      {filteredMedications.map((medication) => (
        <MedicationListItem key={medication.id} medication={medication} />
      ))}
    </div>
  )
}

/**
@description Renders the chart table.

...

@param {bool} viewMedList. Stores whether or not to display
medication list.

@returns {function} renderTable
*/
const renderTable = (props) => {
  const { endDate, medications, rowDisplaySettings, startDate, isPDF } = props

  const tableStartDate = getTableStartDate(startDate, endDate)
  const tableData = getMARChartTableData(
    medications,
    rowDisplaySettings,
    tableStartDate,
    endDate
  )
  const allColumns = generateTableColumns(tableStartDate, endDate, isPDF)
  const scroll = isPDF ? null : { x: 'calc(700px + 50%)' }
  return (
    <div className="lmc-emar-chart">
      {/* <div
                style={{ background: colors.red, height: 40, width: 40 }}
                onClick={handleScroll}
            /> */}
      <TableStyled
        bordered
        columns={allColumns}
        dataSource={tableData}
        pagination={false}
        scroll={scroll}
        sticky={!isPDF}
      />
    </div>
  )
}

// Main function //

/**
@description Renders a MAR Chart

@param {object} endDate. The end date. A dayjs object.

@param {bool} displayLegend. Toggles whether to display the legend.

@param {bool} displayTableHeader. Toggles whether to display the table header.

@param {array} medications. The medications to be rendered.

@param {func} setEndDate. Sets the end date.

@param {func} setStartDate. Sets the start date.

@param {object} startDate. The start date. A dayjs object.

@param {object} todayDate. Today's date. A dayjs object.

@returns {function} MARChart
*/

export default function MARChart(props) {
  // useEffect(() => {
  //     const finalColumn = document.querySelector('.final-column')
  //     finalColumn && finalColumn?.scrollIntoView({ align: { right: 0 } })
  // })

  // const handleScroll = () => {
  //     const finalColumn = document.querySelector('.final-column')
  //     finalColumn && finalColumn?.scrollIntoView({ align: { left: 0 } })
  // }

  const [viewMedList, setViewMedList] = React.useState(false)

  const stateProps = { viewMedList, setViewMedList }

  return (
    <div>
      {renderLegend(props)}
      {renderTableHeader({ ...props, ...stateProps })}
      {viewMedList ? renderMedList(props) : renderTable(props)}
    </div>
  )
}

MARChart.propTypes = {
  displayDatePicker: PropTypes.bool,
  displayLegend: PropTypes.bool,
  displayMedListToggle: PropTypes.bool,
  displayTableHeader: PropTypes.bool,
  endDate: PropTypes.object.isRequired,
  hideScroll: PropTypes.bool,
  medications: PropTypes.array.isRequired,
  rowDisplaySettings: PropTypes.object,
  setEndDate: PropTypes.func,
  setStartDate: PropTypes.func,
  startDate: PropTypes.object.isRequired,
  todayDate: PropTypes.object,
}

const styles = {
  datePickerContainer: {
    display: 'flex',
  },
  footer: {
    display: 'flex',
    marginTop: 26,
  },
  legendContainer: {
    marginBottom: 16,
  },
  PRNBadge: {
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    padding: '3px 12px',
    borderRadius: 15,
  },
  tableHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
}
